export const NFHS_DIGITAL_URL = 'https://rules.nfhsdigital.org/'
export const NFHS_DIGITAL_STORE_URL = 'https://store.nfhsdigital.org/'

export const NFHS_ASPECT_RATIO = 16 / 10
export const NFHS_MIN_WIDTH = 420
export const NFHS_MIN_HEIGHT = 280
/** Phones and small tablets open the reader nearly full-screen instead of a short 16:10 window. */
export const NFHS_COMPACT_MAX_WIDTH = 900
export const NFHS_INSET = 12
export const NFHS_MAXIMIZED_INSET = 8
export const NFHS_PANEL_Z_BASE = 200
export const NFHS_DOCK_Z_BASE = 210
export const NFHS_STACK_OFFSET = 28
export const NFHS_DOCK_WIDTH = 80
export const NFHS_DOCK_HEIGHT = 120

export const NFHS_DEFAULT_COVER = 'data:image/svg+xml,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="80" height="120" viewBox="0 0 80 120">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#0f766e"/>
      <stop offset="1" stop-color="#115e59"/>
    </linearGradient>
  </defs>
  <rect width="80" height="120" rx="8" fill="url(#g)"/>
  <rect x="8" y="12" width="64" height="96" rx="4" fill="#042f2e"/>
  <rect x="14" y="20" width="52" height="6" rx="3" fill="#5eead4" opacity="0.9"/>
  <rect x="14" y="32" width="36" height="4" rx="2" fill="#99f6e4" opacity="0.55"/>
  <text x="40" y="78" text-anchor="middle" fill="#ccfbf1" font-size="11" font-family="Arial, sans-serif" font-weight="700">NFHS</text>
</svg>
`.trim())

export function clamp (value, min, max) {
  return Math.min(max, Math.max(min, value))
}

export function nfhsMaxWidth (viewportWidth = 1280, inset = NFHS_INSET) {
  return Math.max(200, viewportWidth - inset * 2)
}

export function nfhsMaxHeight (viewportHeight = 800, inset = NFHS_INSET) {
  return Math.max(200, viewportHeight - inset * 2)
}

export function nfhsHeightFromWidth (width) {
  return Math.round(width / NFHS_ASPECT_RATIO)
}

export function viewportSize () {
  if (typeof window === 'undefined') {
    return { vw: 1280, vh: 800 }
  }
  const vv = window.visualViewport
  const vw = Math.round(vv?.width || window.innerWidth || 1280)
  const vh = Math.round(vv?.height || window.innerHeight || 800)
  return { vw, vh }
}

export function clampNfhsPanel (panel, vw, vh, inset = NFHS_INSET) {
  const maxWidth = nfhsMaxWidth(vw, inset)
  const maxHeight = nfhsMaxHeight(vh, inset)
  const minWidth = Math.min(NFHS_MIN_WIDTH, maxWidth)
  const minHeight = Math.min(NFHS_MIN_HEIGHT, maxHeight)
  const width = clamp(Number(panel.width) || minWidth, minWidth, maxWidth)
  const height = clamp(Number(panel.height) || minHeight, minHeight, maxHeight)
  const left = clamp(
    Number(panel.left) || inset,
    inset,
    Math.max(inset, vw - width - inset)
  )
  const top = clamp(
    Number(panel.top) || inset,
    inset,
    Math.max(inset, vh - height - inset)
  )
  return { ...panel, left, top, width, height }
}

export function defaultReadPanel (vw, vh, stackIndex = 0) {
  const maxWidth = nfhsMaxWidth(vw)
  const maxHeight = nfhsMaxHeight(vh)
  if (vw <= NFHS_COMPACT_MAX_WIDTH) {
    return clampNfhsPanel({
      width: maxWidth,
      height: maxHeight,
      left: NFHS_INSET,
      top: NFHS_INSET,
    }, vw, vh)
  }
  let width = Math.max(NFHS_MIN_WIDTH, Math.round(maxWidth * 0.82))
  let height = nfhsHeightFromWidth(width)
  if (height > maxHeight) {
    height = maxHeight
    width = clamp(Math.round(height * NFHS_ASPECT_RATIO), NFHS_MIN_WIDTH, maxWidth)
  }
  const offset = (stackIndex % 6) * NFHS_STACK_OFFSET
  return clampNfhsPanel({
    width,
    height,
    left: Math.max(NFHS_INSET, Math.round((vw - width) / 2) - offset),
    top: Math.max(NFHS_INSET, Math.round((vh - height) / 2) + offset),
  }, vw, vh)
}

export function defaultMaximizedPanel (vw, vh) {
  const inset = NFHS_MAXIMIZED_INSET
  return {
    left: inset,
    top: inset,
    width: nfhsMaxWidth(vw, inset),
    height: nfhsMaxHeight(vh, inset),
  }
}

/**
 * Iframe URL for an open window.
 * Preview uses the public store listing (cover, edition, buy info) — not the reader.
 * Read uses rules.nfhsdigital.org/book/{id}. NFHS adds /pdf or /interactive itself.
 * Empty when there is no publication — never frame NFHS home.
 */
export function nfhsOpenReaderUrl (reader) {
  const publication = reader?.publication
  if (!publication?.id) return ''
  if (reader?.mode === 'preview') return nfhsStorePreviewUrl(publication)
  return nfhsDigitalReaderUrl(publication)
}

export function coerceNfhsIsInteractive (value) {
  if (typeof value === 'boolean') return value
  if (value === 'false' || value === 0 || value === '0') return false
  if (value === 'true' || value === 1 || value === '1') return true
  return null
}

export function nfhsPublicationPdfUrl (publication) {
  if (!publication || typeof publication !== 'object') return ''
  return String(publication.pdfURL || publication.pdfUrl || '').trim()
}

/**
 * Kept for publication metadata only. The iframe always loads /book/{id};
 * NFHS Digital chooses /pdf or /interactive after that page loads.
 */
export function nfhsReaderPath (publication) {
  if (!publication || typeof publication !== 'object') return ''
  const existing = String(publication.readerPath || '').trim().toLowerCase()
  if (existing === 'pdf' || existing === 'interactive') return existing
  return ''
}

export function nfhsDigitalReaderUrl (publication) {
  if (!publication?.id) return ''
  return `https://rules.nfhsdigital.org/book/${encodeURIComponent(publication.id)}`
}

/**
 * Page loaded in the iframe before the book.
 * The book's first load is stored on learnapp's current history entry, so
 * NFHS's back control (`history.back()`) would otherwise step through learnapp
 * routes. A prior NFHS document gives that back control an in-frame page.
 */
export function nfhsIframeHistorySeed (url) {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return url
    const seed = `${parsed.origin}/`
    if (seed === url || parsed.pathname === '/') return url
    return seed
  } catch {
    return url
  }
}

/** Keep routing fields when focusing an already-open reader with a slimmer card. */
export function mergeNfhsPublication (current, incoming) {
  if (!incoming) return current || null
  if (!current) return incoming
  const isInteractive = coerceNfhsIsInteractive(incoming.isInteractive)
  const currentInteractive = coerceNfhsIsInteractive(current.isInteractive)
  const pdfURL = nfhsPublicationPdfUrl(incoming) || nfhsPublicationPdfUrl(current) || null
  const merged = {
    ...current,
    ...incoming,
    isInteractive: isInteractive ?? currentInteractive,
    pdfURL,
    readerPath: incoming.readerPath || current.readerPath || null,
    readerUrl: incoming.readerUrl || current.readerUrl || null,
  }
  merged.readerUrl = nfhsDigitalReaderUrl(merged)
  merged.readerPath = nfhsReaderPath(merged) || merged.readerPath || null
  return merged
}

export function nfhsStorePreviewUrl (publication) {
  if (!publication?.id) return ''
  return `https://store.nfhsdigital.org/book/${encodeURIComponent(publication.id)}`
}

/** Cover for the collapsed dock thumbnail, derived from the publication. */
export function nfhsReaderCover (reader) {
  return reader?.publication?.image || reader?.publication?.cover || NFHS_DEFAULT_COVER
}

export function nfhsReaderTitle (reader) {
  return reader?.publication?.title || 'NFHS Publications'
}

export function nfhsBookUrl (bookId) {
  const id = String(bookId || '').trim()
  if (!id) return ''
  return nfhsDigitalReaderUrl({ id })
}

/** Storefront link used for sharing and copy-link, never for the reader iframe. */
export function nfhsStoreBookUrl (bookId) {
  const id = String(bookId || '').trim()
  if (!id) return ''
  return nfhsStorePreviewUrl({ id })
}

/** Reader id is the publication id, so a book maps to exactly one window. */
export function nfhsReaderId (publication) {
  return publication?.id ? String(publication.id) : ''
}

export function createNfhsDigitalReader (options = {}, stackIndex = 0, zIndex = NFHS_PANEL_Z_BASE) {
  const publication = options.publication
  if (!publication?.id) return null
  const { vw, vh } = viewportSize()
  const geom = defaultReadPanel(vw, vh, stackIndex)
  const mode = options.mode === 'preview' ? 'preview' : 'read'
  return {
    id: String(publication.id),
    mode,
    publication,
    collapsed: false,
    maximized: false,
    left: geom.left,
    top: geom.top,
    width: geom.width,
    height: geom.height,
    dockTop: geom.top,
    dockLeft: null,
    dockFloating: false,
    zIndex,
    restore: null,
  }
}

export function createDefaultNfhsDigitalState () {
  return {
    nextZ: NFHS_PANEL_Z_BASE + 1,
    readers: [],
  }
}

export function normalizeNfhsDigitalState (raw) {
  if (raw && Array.isArray(raw.readers)) {
    return {
      nextZ: Number(raw.nextZ) || NFHS_PANEL_Z_BASE + 1,
      readers: raw.readers,
    }
  }
  return createDefaultNfhsDigitalState()
}

export function nextDockTop (readers, preferredTop, vh, ignoreId = null) {
  const taken = (readers || [])
    .filter((reader) => (
      reader.collapsed
      && reader.id !== ignoreId
      && reader.dockLeft == null
    ))
    .map((reader) => reader.dockTop)
  let top = clamp(preferredTop, NFHS_INSET, Math.max(NFHS_INSET, vh - NFHS_DOCK_HEIGHT - NFHS_INSET))
  for (let i = 0; i < 12; i += 1) {
    if (!taken.some((existing) => Math.abs(existing - top) < NFHS_DOCK_HEIGHT + 8)) {
      return top
    }
    top += NFHS_DOCK_HEIGHT + 12
    if (top > vh - NFHS_DOCK_HEIGHT - NFHS_INSET) {
      top = NFHS_INSET
    }
  }
  return top
}

/** @deprecated Kept for older call sites during the reader chrome upgrade. */
export function createDefaultNfhsDigitalWindow () {
  return createDefaultNfhsDigitalState()
}

/** @deprecated */
export function defaultNfhsDigitalBounds (viewportWidth = 1280, viewportHeight = 800) {
  const panel = defaultReadPanel(viewportWidth, viewportHeight, 0)
  return {
    width: panel.width,
    height: panel.height,
    x: panel.left,
    y: panel.top,
  }
}
